import { Fragment, useEffect, useRef, useState } from 'react'
import useFetch from '../../hooks/useFetch'
import { urlApi } from '../../config'
import LoadingIndicator from '../../common/LoadingIndicator'
import ChatBubble from './ChatBubble'
import Input from './Input'
import { formatChatDate, sortChannels } from '../../utils'
import { ADMIN_PROFILE } from '../../config/Chat'
import useAuth from '../../hooks/useAuth'
import {
  applyDeletedMessage,
  generateClientId,
  hasNewServerMessages,
  hasServerId,
  markMessagesRead,
  mergeServerMessages,
  reconcileLocalMessages,
  updateChannelList,
  upsertChannelMessages
} from '../../utils/chatMessages'

export default function Channel({
  selectedChannelId,
  selectedChannel,
  socket,
  channels,
  setChannels,
  socketDisconnected,
  deletedMessage
}) {
  const { getAccessTokenSilently } = useAuth()
  const user = ADMIN_PROFILE.user_id
  const { loading, data, responseOk } = useFetch(`${urlApi}/admin/channels/${selectedChannelId}`)
  const [channel, setChannel] = useState()
  const [messages, setMessages] = useState()
  const [users, setUsers] = useState()
  const [readChannel, setReadChannel] = useState(false)
  const bottomRef = useRef()
  const lastReconciledRef = useRef({ channelId: null, messages: null })

  const readMessages = async () => {
    const token = await getAccessTokenSilently()
    const request = await fetch(`${urlApi}/admin/channels/${selectedChannelId}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(typeof token === 'string' && { 'authorization': `Bearer ${token}` })
      }
    })

    if (request.ok === false) return

    const read = await request.json().then(data => data.read)

    setReadChannel(true)
  }

  useEffect(() => {
    if (readChannel && messages && channel) {
      setMessages(prev => markMessagesRead(prev, user))
      setChannels(prev =>
        sortChannels(
          upsertChannelMessages(prev, { ...channel, messages }, list => markMessagesRead(list, user))
        )
      )
      setReadChannel(false)
    }
  }, [readChannel, messages, channel])

  useEffect(() => {
    const updateMessages = async () => {
      const unreadMessages = data.messages
        .filter(message => message.user_id != user)
        .some(message => !message.read_by?.includes(user))
      if (unreadMessages) {
        await readMessages(data)
      }
    }

    if (responseOk) {
      setChannel(data)
      setMessages(prev => mergeServerMessages(data.messages, prev))
      setUsers(data.users)
      setChannels(updateChannelList(data._id, list => mergeServerMessages(data.messages, list)))
      updateMessages()
    } else if (!loading) {
      setChannel(selectedChannel)
      setMessages(selectedChannel.messages)
      setUsers(selectedChannel.users)
    }
  }, [loading])

  useEffect(() => {
    const updateMessages = async () => {
      const channelMessages = channels.find(channel => channel._id === selectedChannelId)?.messages
      const lastReconciled = lastReconciledRef.current
      if (lastReconciled.channelId === selectedChannelId && lastReconciled.messages === channelMessages) return
      lastReconciledRef.current = { channelId: selectedChannelId, messages: channelMessages }
      setMessages(prev => reconcileLocalMessages(prev, channelMessages))
      if (hasNewServerMessages(messages, channelMessages)) {
        await readMessages(channels.find(channel => channel._id === selectedChannelId))
      }
    }

    updateMessages()
  }, [channels])

  useEffect(() => {
    if (!deletedMessage || deletedMessage.channel_id !== selectedChannelId) return
    setMessages(prev => (prev ? applyDeletedMessage(prev, deletedMessage.message_id) : prev))
  }, [deletedMessage, selectedChannelId])

  const handleSendMessage = text => {
    if (!channel) return
    const clientId = generateClientId()
    socket.current.emit('send', {
      channel_id: channel._id,
      text,
      client_id: clientId
    })
    const newMessage = {
      client_id: clientId,
      text,
      user_id: user,
      date: new Date().toISOString(),
      read_by: [],
      user: {},
      pending: true,
      ...(socketDisconnected && { not_sent: true })
    }
    setMessages(prev => [...(prev || []), newMessage])
    setChannels(prev =>
      sortChannels(upsertChannelMessages(prev, { ...channel, messages }, list => [...list, newMessage]))
    )
  }

  const handleDeleteMessage = message => {
    if (!channel || !hasServerId(message)) return
    socket.current.emit('delete', {
      channel_id: channel._id,
      message_id: message.id
    })
    setMessages(prev => applyDeletedMessage(prev, message.id))
    setChannels(prev =>
      sortChannels(
        upsertChannelMessages(prev, { ...channel, messages }, list => applyDeletedMessage(list, message.id))
      )
    )
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
  }, [messages])

  return (
    <div className='flex flex-col justify-end w-full h-full rounded-tl-xl pb-4'>
      <div className='flex flex-col overflow-auto gap-2 bg-light_gray dark:bg-dk-bg p-3 h-screen'>
        {messages && users ? (
          <>
            <MessageList
              messages={messages}
              user={user}
              users={users}
              handleDeleteMessage={handleDeleteMessage}
            />
            <div ref={bottomRef} />
          </>
        ) : (
          <div className='h-full w-full flex items-center justify-center'>
            <LoadingIndicator />
          </div>
        )}
      </div>
      <Input channel={channel} sendMessage={handleSendMessage} disabled={!channel} />
    </div>
  )
}

const MessageList = ({ messages, user, users, handleDeleteMessage }) => {
  return messages?.map((message, index) => {
    const owner = message.user_id == user
    const sender =
      message.user && message.user.name
        ? message.user
        : users.find(user => user.user_id == message.user_id) ?? ADMIN_PROFILE
    const previousMessage = messages[index - 1]
    const nextMessage = messages.length - 1 == index ? null : messages[index + 1]
    const firstMessageFromSender = !previousMessage || previousMessage.user_id != message.user_id
    const lastMessageFromSender = !nextMessage || nextMessage.user_id != message.user_id
    const sameDay =
      previousMessage &&
      new Date(previousMessage.date).toLocaleDateString() ==
        new Date(message.date).toLocaleDateString()
    const readBy = (message.read_by || [])
      .filter(id => id != message.user_id)
      .map(id => users.find(user => user.user_id == id)?.name)
      .filter(name => name)
      .join(', ')
    return (
      <Fragment key={message.client_id || `${message.id}-${index}`}>
        {!sameDay && <DayDivider day={formatChatDate(message.date)} />}
        <ChatBubble
          handleDeleteMessage={handleDeleteMessage}
          owner={owner}
          sender={sender}
          message={message}
          firstFromSender={firstMessageFromSender}
          lastFromSender={lastMessageFromSender}
          readBy={readBy}
          users={users}
        />
      </Fragment>
    )
  })
}

const DayDivider = ({ day }) => {
  return (
    <div className='w-full flex justify-center sticky top-0'>
      <div className='flex items-center justify-center bg-gray dark:bg-dk-hover rounded-full w-20 sticky my-4'>
        <p className='text-sm text-white py-1 px-3'>{day}</p>
      </div>
    </div>
  )
}
